/**
 * userPrefsApi.ts — MER Algo Footprints v9.2.0
 *
 * Phase 3: thin client wrapper around the /api/user/prefs endpoints
 * (server/userPrefsRoutes.js). Used by:
 *   - App-load hydration (commit 2.7) to read the user's stored horizon
 *   - UI selectors (Session 3) to PATCH on horizon change
 *
 * Style matches the inline fetch pattern used elsewhere in src/features:
 *   credentials: 'include' for session cookie auth, response.ok + JSON
 *   reason for error reporting.
 *
 * NOTE on resilience (G-CLIENT-2 / D-GCLIENT2-2b):
 *   fetchHorizon never throws and never blocks App load, but a transport,
 *   authorization, or server failure is DISTINGUISHABLE from a successful
 *   hydration: the result carries `ok: false` with the never-blocking
 *   DEFAULT_HORIZON so callers may keep rendering the UX default without
 *   misrepresenting the failure as successful preference hydration. A
 *   successful response (which the GET endpoint already resolves to
 *   DEFAULT_HORIZON when no row exists) carries `ok: true`.
 *
 *   saveHorizon throws on failure so the UI layer can surface an error
 *   to the user (e.g. "Couldn't save horizon — try again").
 */

import { TraderHorizon, DEFAULT_HORIZON } from '../types';

const ENDPOINT = '/api/user/prefs';

/**
 * Discriminated horizon-hydration result. `ok: true` means the server
 * successfully answered with a valid horizon (a persisted preference, or the
 * endpoint's own no-row default). `ok: false` means transport/authorization/
 * server failure or a contract-invalid payload; `horizon` then carries the
 * never-blocking UX default and must not be treated as hydrated truth.
 */
export type HorizonFetchResult =
  | { ok: true;  horizon: TraderHorizon }
  | { ok: false; horizon: TraderHorizon };

/**
 * Fetch the user's stored trader horizon. Never throws. Safe to call during
 * app initialization. Failure is explicit via `ok: false`, never silently
 * disguised as a successful default.
 */
export async function fetchHorizon(): Promise<HorizonFetchResult> {
  try {
    const r = await fetch(ENDPOINT, { credentials: 'include' });
    if (!r.ok) return { ok: false, horizon: DEFAULT_HORIZON };
    const data = await r.json();
    const h = data && data.traderHorizon;
    if (h === 'scalp' || h === 'intraday' || h === 'swing' || h === 'position') {
      return { ok: true, horizon: h };
    }
    // Contract-invalid payload — an error condition, not a hydrated default.
    return { ok: false, horizon: DEFAULT_HORIZON };
  } catch {
    return { ok: false, horizon: DEFAULT_HORIZON };
  }
}

/**
 * Save the user's trader horizon. Throws on failure with a string error.
 * Caller should display the error to the user and decide whether to retry.
 */
export async function saveHorizon(horizon: TraderHorizon): Promise<void> {
  let r: Response;
  try {
    r = await fetch(ENDPOINT, {
      method:      'PATCH',
      credentials: 'include',
      headers:     { 'Content-Type': 'application/json' },
      body:        JSON.stringify({ traderHorizon: horizon }),
    });
  } catch (err) {
    throw new Error('NETWORK_ERROR');
  }

  if (!r.ok) {
    let reason = `HTTP_${r.status}`;
    try {
      const data = await r.json();
      if (data && typeof data.reason === 'string') reason = data.reason;
    } catch { /* response wasn't JSON; keep HTTP_xxx */ }
    throw new Error(reason);
  }
}
